import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Italic from '@tiptap/extension-italic';
import Bold from '@tiptap/extension-bold';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';

import { Gapcursor, UndoRedo } from '@tiptap/extensions';
import UniqueID from '@tiptap/extension-unique-id';

import Link from '@tiptap/extension-link';
import HardBreak from '@tiptap/extension-hard-break';

import { BulletList, ListItem } from '@tiptap/extension-list';

import Highlight from '@tiptap/extension-highlight';
import { TableRow } from '@tiptap/extension-table';

import Mathematics from '@tiptap-pro/extension-mathematics';
import { LinkBubbleMenuHandler } from 'mui-tiptap';
import Collaboration, { isChangeOrigin } from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { Snapshot, SnapshotStorage } from '@tiptap-pro/extension-snapshot';
import { SnapshotCompare, SnapshotCompareStorage } from '@tiptap-pro/extension-snapshot-compare';

import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { IdTokenClaims } from 'oidc-client-ts';
import { GuideLinkExtensionRenderOptions, createGuideLinkExtension } from './GuideLink';
import { CitationLinkExtensionRenderOptions, createCitationLinkExtension } from './CitationLink';
import { createUserFromProfile } from '../../../tiptapCollab/collaborationUser';
import { createImageFigureExtension } from './ImageFigure';
import { Extensions } from '@tiptap/core';
import { NodeViewProps } from '@tiptap/react';
import { FC } from 'react';
import { InternalLinkExtensionRenderOptions, createInternalLinkExtension } from './InternalLink';
import { createTableFigureExtension } from './TableFigure';
import { Figcaption } from './Figcaption';
import { createHeadingExtension } from './Heading';
import { createAppExtension } from './App';
import { createChartExtension } from './Chart';
import { createChartFigureExtension } from './ChartFigure';
import { CustomTableCell, CustomTableHeader } from './TableCell';
import { createMathTypeExtension } from './MathType';
import { createStandardLinkExtension, StandardLinkExtensionRenderOptions } from './StandardLink';
import { Table } from './Table';
import { CustomOrderedList } from './OrderedList';
import { CommentsKit, ThreadStorage } from '@tiptap-pro/extension-comments';
import Attachment from './Attachment';
import AttachmentBubbleMenuHandler from './AttachmentBubbleMenuHandler';
import { createTablePastePlugin } from '../../pasting';
import { Extension } from '@tiptap/core';
import { ReferenceCandidateHighlight, ReferenceCandidateTypes } from './ReferenceCandidateHighlight';
import ScrollIntoViewWithOffset from './ScrollIntoViewWithOffset';
import { IndentParagraph } from './IndentParagraph';
import { PageBreak } from './PageBreak';
import { NonBreakingSpaceHighlight } from './NonBreakingSpaceHighlight';
import { DictionaryHighlight } from './DictionaryHighlight';

const CustomLinkExtension = Link.extend({
  inclusive: false,
});

const CustomSubscript = Subscript.extend({
  excludes: 'superscript',
});
const CustomSuperscript = Superscript.extend({
  excludes: 'subscript',
});

declare module '@tiptap/core' {
  interface Storage {
    snapshot: SnapshotStorage;
    snapshotCompare: SnapshotCompareStorage;
    comments: ThreadStorage;
  }
}

export interface SnapshotUpdatePayload {
  versions: Array<{ version: number; name?: string; date: number }>;
  currentVersion: number;
  version: number; // latest version
}

interface ExtensionOptions {
  rendering: ExtensionRenderOptions;
  comments?: CommentsOptions;
  profile?: IdTokenClaims;
  provider?: TiptapCollabProvider;
  onReferenceCandidateClick?: (text: string, type: ReferenceCandidateTypes) => void;
  onSnapshotUpdate?: (payload: SnapshotUpdatePayload) => void;
}

interface ExtensionRenderOptions {
  appView: FC<NodeViewProps>;
  imageFigureView: FC<NodeViewProps>;
  tableFigureView: FC<NodeViewProps>;
  headingView: FC<NodeViewProps>;
  guideLink: GuideLinkExtensionRenderOptions;
  citationLink: CitationLinkExtensionRenderOptions;
  standardLink: StandardLinkExtensionRenderOptions;
  internalLink: InternalLinkExtensionRenderOptions;
  chartFigureView: FC<NodeViewProps>;
  chartView: FC<NodeViewProps>;
  mathTypeView: FC<NodeViewProps>;
}

interface CommentsOptions {
  onClickThread: (id: string | null) => void;
}

const getBaseExtensions = (
  options: ExtensionRenderOptions,
  onReferenceCandidateClick?: ExtensionOptions['onReferenceCandidateClick'],
): Extensions => [
  Document,
  Text,
  Italic,
  Bold,
  Underline,
  Highlight,
  Gapcursor,
  HardBreak,
  ListItem,
  TextAlign.configure({
    types: ['paragraph'],
  }),
  BulletList,
  CustomOrderedList,
  IndentParagraph,
  PageBreak,
  createHeadingExtension(options.headingView),
  createGuideLinkExtension(options.guideLink ?? {}),
  createCitationLinkExtension(options.citationLink ?? {}),
  createStandardLinkExtension(options.standardLink ?? {}),
  createImageFigureExtension(options.imageFigureView),
  createAppExtension(options.appView),
  createChartExtension(options.chartView),
  createChartFigureExtension(options.chartFigureView),
  Table,
  createTableFigureExtension(options.tableFigureView),
  createInternalLinkExtension(options.internalLink ?? {}),
  createMathTypeExtension(options.mathTypeView),
  Attachment,
  CustomTableHeader,
  CustomTableCell,
  TableRow,
  Figcaption,
  CustomSubscript,
  CustomSuperscript,
  Mathematics,
  CustomLinkExtension.configure({
    autolink: true,
    linkOnPaste: true,
    openOnClick: false,
  }),
  LinkBubbleMenuHandler,
  AttachmentBubbleMenuHandler,
  UniqueID.configure({
    attributeName: 'id',
    types: ['paragraph', 'heading', 'tableFigure', 'imageFigure', 'chartFigure', 'bulletList', 'orderedList', 'app'],
    filterTransaction: (transaction) => {
      // Filter out transactions that should not generate new IDs
      // - When the setContent command is called (meta.preventUpdate: true), as this will already have IDs
      // - When the transaction is from another user in collab mode
      return !transaction.getMeta('preventUpdate') && !isChangeOrigin(transaction);
    },
  }),
  Extension.create({
    name: 'tablePasteHandler',
    addProseMirrorPlugins() {
      return [createTablePastePlugin()];
    },
  }),
  ReferenceCandidateHighlight.configure({
    onCandidateClick: onReferenceCandidateClick,
  }),
  ScrollIntoViewWithOffset,
  NonBreakingSpaceHighlight,
  DictionaryHighlight,
];

export const getExtensions = (options: ExtensionOptions): Extensions => {
  const { provider, profile, rendering, comments, onReferenceCandidateClick, onSnapshotUpdate } = options;
  if (!provider) {
    return [...getBaseExtensions(rendering, onReferenceCandidateClick), UndoRedo];
  }
  const user = createUserFromProfile(profile);
  const collabExtensions = [
    Collaboration.configure({
      document: provider.document,
    }),
    CollaborationCaret.configure({
      provider: provider,
      user,
    }),
    Snapshot.configure({
      provider,
      onUpdate: onSnapshotUpdate,
    }),
    CommentsKit.configure({
      provider: provider,
      useLegacyWrapping: false,
      onClickThread(id) {
        if (comments?.onClickThread) {
          comments.onClickThread(id);
        }
      },
    }),
  ];
  return [...getBaseExtensions(rendering, onReferenceCandidateClick), ...collabExtensions];
};

export const getExtensionsForPreview = (options: ExtensionOptions): Extensions => {
  const { provider, rendering } = options;
  return [
    ...getBaseExtensions(rendering),
    CommentsKit,
    SnapshotCompare.configure({
      provider,
    }),
  ];
};

export default getExtensions;
